/**
 * card.jsx — Card container primitives for grouping related content.
 *
 * Exports: Card, CardHeader, CardTitle, CardContent
 *
 *   Card        — Outer container. Rounded border with card background and shadow.
 *   CardHeader  — Top section with flex-column layout and 1.5-unit spacing between children.
 *   CardTitle   — Large semibold heading rendered as <h3>.
 *   CardContent — Body section with padding (no top padding to avoid double-gap with CardHeader).
 *
 * All components forward refs and accept a className prop for style overrides.
 */
import * as React from "react"
import { cn } from "../../lib/utils"

/**
 * Card — Root container with border, rounded corners, and card-themed background.
 * @param {string} [props.className] - Additional Tailwind classes.
 * @param {React.Ref} ref - Forwarded ref.
 */
const Card = React.forwardRef(({ className, ...props }, ref) => (
  <div
    ref={ref}
    className={cn(
      "rounded-lg border bg-card text-card-foreground shadow-sm",
      className
    )}
    {...props}
  />
))
Card.displayName = "Card"

/**
 * CardHeader — Top section of a Card. Stacks children vertically with spacing.
 * @param {string} [props.className] - Additional Tailwind classes.
 * @param {React.Ref} ref - Forwarded ref.
 */
const CardHeader = React.forwardRef(({ className, ...props }, ref) => (
  <div
    ref={ref}
    className={cn("flex flex-col space-y-1.5 p-6", className)}
    {...props}
  />
))
CardHeader.displayName = "CardHeader"

/**
 * CardTitle — Semibold heading rendered as <h3>. Typically placed inside CardHeader.
 * @param {string} [props.className] - Additional Tailwind classes.
 * @param {React.Ref} ref - Forwarded ref.
 */
const CardTitle = React.forwardRef(({ className, ...props }, ref) => (
  // eslint-disable-next-line jsx-a11y/heading-has-content
  <h3
    ref={ref}
    className={cn(
      "text-2xl font-semibold leading-none tracking-tight",
      className
    )}
    {...props}
  />
))
CardTitle.displayName = "CardTitle"

/**
 * CardContent — Main body area of a Card. Has padding on all sides except top.
 * @param {string} [props.className] - Additional Tailwind classes.
 * @param {React.Ref} ref - Forwarded ref.
 */
const CardContent = React.forwardRef(({ className, ...props }, ref) => (
  <div ref={ref} className={cn("p-6 pt-0", className)} {...props} />
))
CardContent.displayName = "CardContent"

export { Card, CardHeader, CardTitle, CardContent }
